import React, { CSSProperties, useEffect, useState } from 'react';
import { TerminalTabloElementSettings } from '../../../types/Admin/TerminalAppearanceTypes';
import { baseSoundURL } from '../../../config';
import styles from './TabloSlider.module.scss';
import { isAndroidTabloDevice } from '../../../utils/QueuePage/tabloDevice';

const DEFAULT_SLIDE_INTERVAL_SEC = 5;
const MIN_INTERVAL_SEC = 1;
const MAX_INTERVAL_SEC = 120;
const isAndroidTablo = isAndroidTabloDevice();

const normalizeMediaUrl = (url: string) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url) || url.startsWith('//')) return url;
  const separator = url.startsWith('/') ? '' : '/';
  return `${baseSoundURL}${separator}${url}`;
};

const getObjectFit = (fitMode: string | null | undefined): CSSProperties['objectFit'] => {
  switch (fitMode) {
    case 'contain':
      return 'contain';
    case 'cover':
      return 'cover';
    case 'stretch':
      return 'fill';
    case 'none':
      return 'none';
    case 'scale':
      return 'scale-down';
    case 'width':
    case 'height':
    default:
      return 'contain';
  }
};

type TabloSliderProps = {
  element: TerminalTabloElementSettings;
};

export const TabloSlider = ({ element }: TabloSliderProps) => {
  const urls = element.imageUrls ?? [];
  const [currentIndex, setCurrentIndex] = useState(0);
  const objectFit = getObjectFit(element.fitMode);
  const intervalSec = element.slideIntervalSeconds ?? DEFAULT_SLIDE_INTERVAL_SEC;
  const intervalMs =
    Math.min(
      MAX_INTERVAL_SEC,
      Math.max(MIN_INTERVAL_SEC, Number(intervalSec) || DEFAULT_SLIDE_INTERVAL_SEC)
    ) *
    1000 *
    (isAndroidTablo ? 1.5 : 1);

  useEffect(() => {
    if (urls.length <= 1) return;
    const id = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % urls.length);
    }, intervalMs);
    return () => clearInterval(id);
  }, [urls.length, intervalMs]);

  if (urls.length === 0) {
    return <div className={styles.container} />;
  }

  const currentUrl = urls[currentIndex];
  const normalizedUrl = normalizeMediaUrl(currentUrl);

  return (
    <div className={styles.container}>
      <img
        src={normalizedUrl}
        alt=""
        className={styles.image}
        style={{ objectFit }}
        decoding="async"
        loading="eager"
      />
    </div>
  );
};
